import React from 'react';
import { LucideIcon } from 'lucide-react';

export type IconVariant = 'blue' | 'sky' | 'cyan' | 'emerald' | 'indigo' | 'amber' | 'rose' | 'slate' | 'gold' | 'teal';
export type IconSize = 'xs' | 'sm' | 'md' | 'lg';

interface IconBadgeProps {
  icon: LucideIcon;
  variant?: IconVariant;
  size?: IconSize;
  active?: boolean;
  className?: string;
  strokeWidth?: number;
}

const variantStyles: Record<
  IconVariant,
  {
    inactive: string;
    active: string;
    iconInactive: string;
    iconActive: string;
  }
> = {
  blue: {
    inactive: 'bg-slate-100 border-slate-200 shadow-2xs',
    active: 'bg-slate-900 border-slate-800 text-white shadow-xs',
    iconInactive: 'text-slate-700 group-hover:text-slate-900',
    iconActive: 'text-white'
  },
  sky: {
    inactive: 'bg-slate-100 border-slate-200 shadow-2xs',
    active: 'bg-slate-900 border-slate-800 text-white shadow-xs',
    iconInactive: 'text-slate-700 group-hover:text-slate-900',
    iconActive: 'text-white'
  },
  cyan: {
    inactive: 'bg-slate-100 border-slate-200 shadow-2xs',
    active: 'bg-slate-900 border-slate-800 text-white shadow-xs',
    iconInactive: 'text-slate-700 group-hover:text-slate-900',
    iconActive: 'text-white'
  },
  emerald: {
    inactive: 'bg-emerald-50 border-emerald-200 shadow-2xs',
    active: 'bg-emerald-600 border-emerald-700 text-white shadow-xs',
    iconInactive: 'text-emerald-700 group-hover:text-emerald-800',
    iconActive: 'text-white'
  },
  teal: {
    inactive: 'bg-slate-100 border-slate-200 shadow-2xs',
    active: 'bg-slate-900 border-slate-800 text-white shadow-xs',
    iconInactive: 'text-slate-700 group-hover:text-slate-900',
    iconActive: 'text-white'
  },
  indigo: {
    inactive: 'bg-slate-100 border-slate-200 shadow-2xs',
    active: 'bg-slate-900 border-slate-800 text-white shadow-xs',
    iconInactive: 'text-slate-700 group-hover:text-slate-900',
    iconActive: 'text-white'
  },
  amber: {
    inactive: 'bg-amber-50 border-amber-200 shadow-2xs',
    active: 'bg-amber-500 border-amber-600 text-white shadow-xs',
    iconInactive: 'text-amber-700 group-hover:text-amber-800',
    iconActive: 'text-white'
  },
  gold: {
    inactive: 'bg-amber-50 border-amber-200 shadow-2xs',
    active: 'bg-amber-500 border-amber-600 text-white shadow-xs',
    iconInactive: 'text-amber-700 group-hover:text-amber-800',
    iconActive: 'text-white'
  },
  rose: {
    inactive: 'bg-rose-50 border-rose-200 shadow-2xs',
    active: 'bg-rose-600 border-rose-700 text-white shadow-xs',
    iconInactive: 'text-rose-700 group-hover:text-rose-800',
    iconActive: 'text-white'
  },
  slate: {
    inactive: 'bg-slate-100 border-slate-200 shadow-2xs',
    active: 'bg-slate-800 border-slate-700 text-white shadow-xs',
    iconInactive: 'text-slate-600 group-hover:text-slate-800',
    iconActive: 'text-white'
  }
};

const sizeStyles: Record<
  IconSize,
  { container: string; icon: string }
> = {
  xs: {
    container: 'w-5 h-5 rounded-md',
    icon: 'w-3 h-3'
  },
  sm: {
    container: 'w-6 h-6 rounded-lg',
    icon: 'w-3.5 h-3.5'
  },
  md: {
    container: 'w-8 h-8 rounded-xl',
    icon: 'w-4 h-4'
  },
  lg: {
    container: 'w-9 h-9 rounded-xl',
    icon: 'w-4.5 h-4.5'
  }
};

export const IconBadge: React.FC<IconBadgeProps> = ({
  icon: Icon,
  variant = 'blue',
  size = 'sm',
  active = false,
  className = '',
  strokeWidth = 2
}) => {
  const currentVariant = variantStyles[variant] || variantStyles.blue;
  const currentSize = sizeStyles[size] || sizeStyles.sm;

  return (
    <div
      className={`relative flex items-center justify-center shrink-0 border transition-all duration-150 overflow-hidden ${
        currentSize.container
      } ${active ? currentVariant.active : currentVariant.inactive} ${className}`}
    >
      <Icon
        className={`shrink-0 transition-colors duration-150 ${currentSize.icon} ${
          active ? currentVariant.iconActive : currentVariant.iconInactive
        }`}
        strokeWidth={strokeWidth}
      />
    </div>
  );
};
